/** @format */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  PLATFORM_ID,
  Renderer2,
  Signal,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  CompareBarProduct,
  CompareItemAttribute,
  CompareProductDetails,
  CompareProductItem,
  ProductAvailabilityCollection,
} from '@shared/data-access/interfaces';
import {
  HomefieldIconLibraryModule,
  HomefieldIconsLibraryService,
  homefieldIconSignAddOutline,
} from '@dsg-tech/homefield-angular-icons';
import { Subject, debounceTime } from 'rxjs';

import { ApiFailureComponent } from './api-failure/api-failure.component';
import { AttributeSectionComponent } from './attribute-section/attribute-section.component';
import { CarouselButtonComponent } from '@shared/components';
import { CompareGridProductCardComponent } from './compare-grid-product-card/compare-grid-product-card.component';
import { CompareService } from '@compare/data-access';
import { DeviceService } from '@shared/data-access/services';
import { FulfillmentSectionComponent } from './fullfilment-section/fulfillment-section.component';
import { NoProductsToCompareComponent } from './no-products/no-products.component';
import { SkeletonAttributeSectionComponent } from './skeleton-attribute-section/skeleton-attribute-section.component';
import { SkeletonSpecsSectionComponent } from './skeleton-specs-section/skeleton-specs-section.component';
import { SpecsSectionComponent } from './specs-section/specs-section.component';
import { isPlatformServer } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

type GridSection =
  | 'productCardsGridSection'
  | 'fulfillmentGridSection'
  | 'attributesGridSection'
  | 'specsGridSection';

@Component({
  selector: 'compare-components-compare-grid',
  imports: [
    AttributeSectionComponent,
    CarouselButtonComponent,
    CompareGridProductCardComponent,
    FulfillmentSectionComponent,
    SpecsSectionComponent,
    SkeletonAttributeSectionComponent,
    SkeletonSpecsSectionComponent,
    HomefieldIconLibraryModule,
    ApiFailureComponent,
    NoProductsToCompareComponent,
  ],
  templateUrl: './compare-grid.component.html',
  styleUrl: './compare-grid.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'hmf-display-block',
  },
})
export class CompareGridComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly deviceService = inject(DeviceService);
  private readonly compareService = inject(CompareService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly hmfIconService = inject(HomefieldIconsLibraryService);
  private readonly renderer = inject(Renderer2);
  private readonly platformId = inject(PLATFORM_ID);

  readonly location = input<'PLP' | 'PDP'>('PLP');
  readonly compareProductDetails = input.required<CompareProductDetails | null>();
  readonly compareBarProducts = input.required<CompareBarProduct[]>();
  readonly compareProductsAvailability = input<ProductAvailabilityCollection | null>(null);
  readonly compareGridLoading = input.required<boolean>();
  readonly compareGridApiFailed = input.required<boolean>();

  readonly productRemoved = output<string>();
  readonly closeCompareGrid = output<void>();
  readonly tryAgainClicked = output<void>();

  readonly productCardsGridSection =
    viewChild<ElementRef<HTMLDivElement>>('productCardsGridSection');

  readonly specsGridSection = viewChild<ElementRef<HTMLDivElement>>('specsGridSection');
  readonly fulfillmentGridSection = viewChild<ElementRef<HTMLDivElement>>('fulfillmentGridSection');

  readonly attributesGridSection = viewChild<ElementRef<HTMLDivElement>>('attributesGridSection');
  readonly scrollIndicator = viewChild<ElementRef<HTMLDivElement>>('scrollIndicator');
  readonly hiddenIntersectionElement = viewChild<ElementRef<HTMLElement>>(
    'hiddenIntersectionElement',
  );

  private readonly resizeSubject = new Subject<void>();

  readonly coalescedProductDetails: Signal<CompareProductItem[]> = computed(() => {
    const products = this.compareProductDetails()?.products;

    if (!products?.length) {
      return [];
    }

    return this.compareService.coalesceAllProductAttributes(products);
  });

  readonly shouldShowFulfillmentSection: Signal<boolean> = computed(() => {
    // don't show fulfillment section on PDP
    if (this.location() === 'PDP') {
      return false;
    }

    // if there is no availability information, don't show fulfillment section
    const fulfillmentData = this.compareProductsAvailability();

    if (!fulfillmentData) {
      return false;
    }

    return Object.keys(fulfillmentData.ecodeMessagingMap || {}).length > 0;
  });

  readonly attributeSectionExpandedList = signal<{ [key: string]: boolean }>({});
  readonly currentElement = signal<GridSection>('productCardsGridSection');
  readonly deviceType = signal('');
  readonly isDrawerScrollable = signal(false);
  readonly isScrollLeftAllowed = signal(false);
  readonly isScrollRightAllowed = signal(false);
  readonly imageAreCompacted = signal(false);
  readonly isSticky = signal(false);
  readonly apiRetryCounter = signal(0);

  constructor() {
    this.hmfIconService.registerIcons([homefieldIconSignAddOutline]);
    effect(() => {
      this.setGridTemplateAttributeRows();
    });

    afterNextRender(() => {
      this.resizeSubject
        .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(200))
        .subscribe(() => {
          this.setDrawerScrollSettings();
        });

      // giving a chance to the drawer to completely open
      // let the first execution thread pass over this function, then let the next one call this function
      setTimeout(() => {
        this.setDrawerScrollSettings();
      }, 0);
    });
  }

  verticalScrolling(): void {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    const drawerScroll = document.getElementById('drawer-content');

    if (!drawerScroll) {
      return;
    }

    if (drawerScroll.scrollTop > 0) {
      this.imageAreCompacted.set(true);
      this.isSticky.set(true);
    } else {
      this.imageAreCompacted.set(false);
      this.isSticky.set(false);
    }
  }

  private get sectionElements(): Record<GridSection, HTMLDivElement | undefined> {
    return {
      productCardsGridSection: this.productCardsGridSection()?.nativeElement,
      fulfillmentGridSection: this.fulfillmentGridSection()?.nativeElement,
      attributesGridSection: this.attributesGridSection()?.nativeElement,
      specsGridSection: this.specsGridSection()?.nativeElement,
    };
  }

  onHorizontalScroll(event: Event): void {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    for (const [name, element] of Object.entries(this.sectionElements)) {
      if (name !== this.currentElement() && !!element) {
        element.scrollLeft = (event.target as Element).scrollLeft;
      }
    }

    this.updateScrollIndicator();
  }

  scrollToSide(direction: 'left' | 'right'): void {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    // determine the scroll distance based on the number of items in the compare bar
    // this is to ensure that the scroll distance is equal to the width of one item
    // in the compare bar, so that when the user clicks the scroll button, it scrolls one item at a time
    const scrollWidth = this.sectionElements.productCardsGridSection?.scrollWidth;
    const scrollLeft = this.sectionElements.productCardsGridSection?.scrollLeft;
    if (!scrollWidth || scrollLeft === undefined) {
      return;
    }

    const scrollDistance = scrollWidth / this.compareBarProducts().length;

    const scrollAmount =
      direction === 'right' ? scrollLeft + scrollDistance : scrollLeft - scrollDistance;

    for (const element of Object.values(this.sectionElements)) {
      if (element) {
        element.scrollTo({ left: scrollAmount, behavior: 'smooth' });
      }
    }
  }

  // As the user touches or mouses over the upper or lower section of the drawer,
  // we need to update the scroll position of the other section
  updateCurrentElement(element: GridSection): void {
    this.currentElement.set(element);
  }

  onDrawerClosed(): void {
    this.isSticky.set(false);
    this.isDrawerScrollable.set(false);
    this.isScrollRightAllowed.set(false);
    this.isScrollLeftAllowed.set(false);
  }

  removeProductFromCompareGrid(ecode: string, index: number): void {
    const productNameSectionNodes: NodeListOf<HTMLElement> | undefined =
      this.productCardsGridSection()?.nativeElement.querySelectorAll(
        '.compare-product-name-section',
      );

    if (!productNameSectionNodes) {
      return;
    }

    // if there are multiple product cards, we need to focus on the card that will end up as the first on the list
    if (productNameSectionNodes.length > 1) {
      const productNameSection: HTMLElement =
        index === 0 ? productNameSectionNodes[1] : productNameSectionNodes[0];
      productNameSection.focus();
    }

    this.productRemoved.emit(ecode);
    this.updateScrollIndicator();
  }

  closeDrawer(): void {
    this.closeCompareGrid.emit();
  }

  handleAttributeExpand(event: { expanded: boolean; identifier: string }): void {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    const newAttributeSectionExpandedList = {
      ...this.attributeSectionExpandedList(),
      [event.identifier]: event.expanded,
    };

    this.attributeSectionExpandedList.set(newAttributeSectionExpandedList);
  }

  @HostListener('window:resize')
  onResize(): void {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    this.resizeSubject.next();
  }

  private setDrawerScrollSettings(): void {
    this.deviceType.set(this.deviceService.getDeviceType());
    this.updateScrollIndicator();
  }

  private updateScrollIndicator(): void {
    /**
     * If not browser environment or grid isn't rendered (i.e. the API call failed or 0 products), we should short
     * circuit the function so we don't attempt to use an element that doesn't exist (which would trigger an error.)
     */
    if (isPlatformServer(this.platformId) || !this.productCardsGridSection()) {
      return;
    }

    this.setDrawerHorizontallyScrollable();
    this.setHorizontalScrollbarPosition();
  }

  private setDrawerHorizontallyScrollable(): void {
    // this compares the scrollWidth (including overflow) to the clientWidth (visible area)
    // to determine whether to show the custom-made scroll bar
    const scrollWidth = this.productCardsGridSection()?.nativeElement.scrollWidth;
    const clientWidth = this.productCardsGridSection()?.nativeElement.clientWidth;

    if (scrollWidth === undefined || clientWidth === undefined) {
      return;
    }

    const isScrollable = scrollWidth > clientWidth;
    this.isDrawerScrollable.set(isScrollable);
    this.cdr.detectChanges();
  }

  private setHorizontalScrollbarPosition(): void {
    // this is to calculate the percentage scrolled horizontally to decide whether
    // to show the left or right buttons of the scroll indicator
    const scrollWidth = this.productCardsGridSection()?.nativeElement.scrollWidth;
    const clientWidth = this.productCardsGridSection()?.nativeElement.clientWidth;
    const scrollLeft = this.productCardsGridSection()?.nativeElement.scrollLeft;

    if (scrollWidth === undefined || clientWidth === undefined || scrollLeft === undefined) {
      return;
    }

    const scrollWidthDifference = scrollWidth - clientWidth;

    const scrollPercentage = scrollLeft / scrollWidthDifference;
    const scrollBarMargins = this.deviceType() === 'mobile' ? 32 : 48;

    const indicatorWidth = (clientWidth / scrollWidth) * clientWidth;
    this.isScrollRightAllowed.set(scrollPercentage < 1);
    this.isScrollLeftAllowed.set(scrollPercentage > 0);

    // if the drawer is scrollable, we show a scrollbar below the upper part to indicate to the user the scroll position
    // this horizontal scrollbar is not interactible, the user is expected to use the buttons or the touch screen if mobile device is used
    const scrollIndicatorRef = this.scrollIndicator()?.nativeElement;
    if (!scrollIndicatorRef) {
      return;
    }
    if (this.isDrawerScrollable()) {
      scrollIndicatorRef.style.width = `${indicatorWidth}px`;
      scrollIndicatorRef.style.transform = `translateX(${scrollPercentage * (clientWidth - indicatorWidth - scrollBarMargins)}px)`;
    }

    // we need to detect changes here because we are updating css styles using javascript
    // and it is not automatically changing the reference of the scroll indicator property in the DOM
    this.cdr.detectChanges();
  }

  private setGridTemplateAttributeRows(): void {
    if (isPlatformServer(this.platformId) || !this.coalescedProductDetails()?.length) {
      return;
    }

    // we need to set the grid template rows for the specs and attributes sections
    // based on the total number of attributes across all products
    const descAttrCount = this.coalescedProductDetails()[0]?.style?.descriptiveAttributes.length;
    const defAttrCount = this.coalescedProductDetails()[0]?.style?.definingAttributes.length;

    // the number of rows in the css grid will be equal to the number of attributes
    const specRowsVal = `repeat(${descAttrCount}, auto)`;
    const attrRowsVal = `repeat(${defAttrCount}, auto)`;
    const gtr = 'grid-template-rows';

    // this sets the grid-template-rows style rule for the attributes grid and the specs grid
    const specsRef = this.specsGridSection?.()?.nativeElement;
    const attrRef = this.attributesGridSection?.()?.nativeElement;

    if (specsRef) {
      this.renderer.setStyle(specsRef, gtr, specRowsVal);
    }
    if (attrRef) {
      this.renderer.setStyle(attrRef, gtr, attrRowsVal);
    }
  }

  trackByCompareBarProduct(index: number, item: CompareBarProduct): string {
    return item.ecode ? item.ecode : `${index.toString()}-empty`;
  }

  protected trackByCompareItemAttrribute(index: number, item: CompareItemAttribute): string {
    return `${index.toString()}-${item.identifier}`;
  }

  onTryAgain(): void {
    this.apiRetryCounter.update((c) => c + 1);
    this.tryAgainClicked.emit();
  }
}
